import { type CSSProperties, useId } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import type { CatalogFacets, TemplateDetail } from "../../../core/types";
import { categoryLabelMap, templateColor } from "../category-style";
import { templateDetailPath } from "../use-template";

/**
 * What a template detail says about the template besides its prompt, shared by the member
 * detail (TemplateDetailView) and the signed-out one (PublicTemplateDetailPage): whether it
 * is a team, its category, industries, keywords and capabilities as labelled chips, a team's
 * members and the teams a single sits in. Labels come from the shared vocabulary that the
 * facets carry (content/taxonomy.yaml), so no raw key and no library name is ever shown
 * (workflows/V5-Template-Organization.md Rev 2 §R2.2 "Cards and detail pages").
 */

export type TemplateRef = TemplateDetail["members"][number];

/** The template fields these pieces read; all optional, since an older payload may lack them. */
export type TemplateFacetFields = {
  categoryKey?: string | null;
  tagKeys?: string[];
  industryKeys?: string[];
  capabilityKeys?: string[];
  kind?: TemplateDetail["kind"];
  members?: TemplateRef[];
  memberOf?: TemplateRef[];
};

export type FacetLabels = {
  categories: Map<string, string>;
  industries: Map<string, string>;
  keywords: Map<string, string>;
  capabilities: Map<string, string>;
  /** The vocabulary's category keys in file order: what `templateColor` positions by. */
  categoryOrder: readonly string[];
};

function termMap(terms: { key: string; label: string }[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const term of terms ?? []) if (!map.has(term.key)) map.set(term.key, term.label);
  return map;
}

/**
 * Key-to-label maps for each facet, all from the shared vocabulary (content/taxonomy.yaml);
 * a library's own lists are always empty (wire 5.15.0) and are not read. A key nobody labels
 * falls back to itself at the call site.
 */
export function facetLabels(facets: CatalogFacets | undefined): FacetLabels {
  const vocabulary = facets?.vocabulary;
  return {
    categories: categoryLabelMap(facets),
    industries: termMap(vocabulary?.industries),
    keywords: termMap(vocabulary?.keywords),
    capabilities: termMap(vocabulary?.capabilities),
    categoryOrder: (vocabulary?.categories ?? []).map((term) => term.key),
  };
}

const chipSty: CSSProperties = {
  padding: "2px 10px",
  background: "var(--brand-x-f4efe2)",
  border: "var(--brand-bw-2) solid var(--brand-line-2)",
  borderRadius: "var(--brand-r-999)",
  fontWeight: 700,
  fontSize: "11px",
  color: "var(--brand-lavender-mid)",
  listStyle: "none",
};

type Chip = {
  facet: "category" | "industry" | "keyword" | "capability";
  key: string;
  label: string;
};

/** The chips in card order: category, industries, keywords, then capabilities. */
export function facetChips(template: TemplateFacetFields, labels: FacetLabels): Chip[] {
  const chips: Chip[] = [];
  if (template.categoryKey) {
    const key = template.categoryKey;
    chips.push({ facet: "category", key, label: labels.categories.get(key) ?? key });
  }
  for (const key of template.industryKeys ?? []) {
    chips.push({ facet: "industry", key, label: labels.industries.get(key) ?? key });
  }
  for (const key of template.tagKeys ?? []) {
    chips.push({ facet: "keyword", key, label: labels.keywords.get(key) ?? key });
  }
  for (const key of template.capabilityKeys ?? []) {
    chips.push({ facet: "capability", key, label: labels.capabilities.get(key) ?? key });
  }
  return chips;
}

export function TemplateFacetChips({
  template,
  labels,
  style,
}: {
  template: TemplateFacetFields;
  labels: FacetLabels;
  style?: CSSProperties;
}) {
  const intl = useIntl();
  const chips = facetChips(template, labels);
  if (chips.length === 0) return null;
  return (
    <ul
      aria-label={intl.formatMessage({ id: "tpl.detail.facets" })}
      style={{ display: "flex", gap: "6px", flexWrap: "wrap", margin: 0, padding: 0, ...style }}
    >
      {chips.map((chip) => (
        <li
          key={`${chip.facet}:${chip.key}`}
          data-facet={chip.facet}
          style={
            chip.facet === "category"
              ? {
                  ...chipSty,
                  borderLeft: `4px solid ${templateColor(chip.key, labels.categoryOrder)}`,
                }
              : chipSty
          }
        >
          {chip.label}
        </li>
      ))}
    </ul>
  );
}

/** "Team · 4 members" or "Single agent", for the line under a template's name. */
export function TemplateKindLine({
  template,
  style,
  className,
}: {
  template: TemplateFacetFields;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div data-template-kind={template.kind ?? "single"} className={className} style={style}>
      {template.kind === "team" ? (
        <FormattedMessage
          id="tpl.detail.kind.team"
          values={{ count: template.members?.length ?? 0 }}
        />
      ) : (
        <FormattedMessage id="tpl.detail.kind.single" />
      )}
    </div>
  );
}

const sectionLabelSty: CSSProperties = {
  fontFamily: "var(--brand-font-display)",
  fontWeight: 700,
  fontSize: "11px",
  letterSpacing: "1px",
  color: "var(--brand-muted)",
  margin: "18px 0 8px",
  textTransform: "uppercase",
};

const refLinkSty: CSSProperties = {
  display: "inline-block",
  padding: "4px 12px",
  background: "var(--brand-card)",
  border: "var(--brand-bw-2) solid var(--brand-ink)",
  borderRadius: "var(--brand-r-999)",
  fontFamily: "var(--brand-font-display)",
  fontWeight: 800,
  fontSize: "12.5px",
  color: "var(--brand-ink)",
  textDecoration: "none",
};

function TemplateRefSection({
  titleId,
  refs,
  headingLevel,
  section,
}: {
  titleId: "tpl.detail.members" | "tpl.detail.usedInTeams";
  refs: TemplateRef[];
  headingLevel: 2 | 3;
  section: "members" | "member-of";
}) {
  const location = useLocation();
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} data-template-section={section} style={{ flexShrink: 0 }}>
      <Heading id={headingId} style={sectionLabelSty}>
        <FormattedMessage id={titleId} />
      </Heading>
      <ul style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: 0, padding: 0 }}>
        {refs.map((ref) => (
          <li key={`${ref.libraryKey}/${ref.slug}`} style={{ listStyle: "none" }}>
            {/* The current location state rides along: opened over the gallery, the next
                detail opens over it too; opened standalone, it stays standalone. */}
            <Link
              to={templateDetailPath(ref.libraryKey, ref.slug)}
              state={location.state}
              style={refLinkSty}
            >
              {ref.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A team's members in roster order, and the teams a single sits in, each linking to that
 * template's detail page. Renders nothing for a single that no team names.
 */
export function TemplateTeamSections({
  template,
  headingLevel,
}: {
  template: TemplateFacetFields;
  headingLevel: 2 | 3;
}) {
  const members = template.kind === "team" ? (template.members ?? []) : [];
  const memberOf = template.memberOf ?? [];
  return (
    <>
      {members.length > 0 ? (
        <TemplateRefSection
          titleId="tpl.detail.members"
          refs={members}
          headingLevel={headingLevel}
          section="members"
        />
      ) : null}
      {memberOf.length > 0 ? (
        <TemplateRefSection
          titleId="tpl.detail.usedInTeams"
          refs={memberOf}
          headingLevel={headingLevel}
          section="member-of"
        />
      ) : null}
    </>
  );
}
